import React from "react";
import { apiStokMenunggu, apiProsesAntreanStok, apiLepasPaksa, formatApiErrorDetail } from "@/lib/api";
import { qty } from "@/lib/format";
import { Loader2, Play } from "lucide-react";
import * as W from "@/lib/waktu";

const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const LABEL = { DP: "DP", ACC: "ACC customer", STOK: "stok bahan" };

// F1.5-F: orders whose material is not taken yet, oldest first (the order in
// which incoming stock serves them). Finance sees what to buy; the owner may
// force one through (stock may go below zero), with a reason.
export default function StokMenunggu({ isOwner, bolehProses, onChanged }) {
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => apiStokMenunggu().then((x) => { setRows(x); setError(null); }).catch((e) => setError(errText(e))), []);
  React.useEffect(() => { load(); }, [load]);
  const proses = async () => {
    setBusy(true);
    try { const r = await apiProsesAntreanStok(); setNotice(`${r.dilepas.length} SPK masuk produksi, ${r.menunggu} masih menunggu stok`); load(); onChanged?.(); }
    catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };
  const paksa = async (r) => {
    const alasan = window.prompt(`Lepas paksa ${r.nomor_spk}? Stok bisa menjadi minus. Alasan (wajib, min. 5 karakter):`);
    if (!alasan) return;
    try { await apiLepasPaksa(r.spk_id, alasan); setNotice(`${r.nomor_spk} masuk produksi (lepas paksa)`); load(); onChanged?.(); }
    catch (e) { setError(errText(e)); }
  };
  return (
    <div data-testid="stok-menunggu" className="space-y-3">
      <div className="flex items-center gap-3">
        <p className="text-sm text-slate-400 flex-1">Pesanan yang bahannya belum dipotong, urut dari yang paling lama. Saat stok masuk (pembelian/penyesuaian), pesanan yang DP-nya terpenuhi otomatis masuk produksi berurutan.</p>
        {bolehProses && <button data-testid="stok-proses" onClick={proses} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 px-3 py-2 text-xs text-slate-200 hover:border-amber-500/50 disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Proses antrean</button>}
      </div>
      {notice && <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">{notice}</div>}
      {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
      {!rows ? <p className="text-slate-400">Memuat…</p> : (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
              <th className="px-3 py-3">Masuk</th><th className="px-3 py-3">SPK / Faktur</th><th className="px-3 py-3">Customer</th><th className="px-3 py-3">Menunggu</th><th className="px-3 py-3">Bahan</th><th /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.spk_id} data-testid={`menunggu-${r.nomor_spk}`} className="border-b border-slate-800/60 align-top">
                  <td className="px-3 py-2 text-xs font-mono">{W.waktu(r.created_at)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-amber-300">{r.nomor_spk}<span className="block text-slate-400">{r.nomor_faktur}</span></td>
                  <td className="px-3 py-2">{r.customer}</td>
                  <td className="px-3 py-2 text-xs text-amber-300">{r.menunggu.map((k) => LABEL[k]).join(" + ")}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.bahan.map((b) => {
                      const k = r.kurang.find((x) => x.kode === b.kode);
                      return <div key={b.bahan_id}>{b.kode} {qty(b.qty_milli)} {b.satuan || ""}{k ? <span className="text-red-300"> · {k.antre ? "antre di belakang pesanan lebih lama" : `stok ${qty(k.saldo_milli)}`}</span> : null}</div>;
                    })}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isOwner && r.menunggu.length === 1 && r.menunggu[0] === "STOK" && <button data-testid={`paksa-${r.nomor_spk}`} onClick={() => paksa(r)} className="rounded border border-red-500/40 px-2 py-1 text-xs text-red-300">Lepas paksa</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <p className="p-4 text-sm text-slate-500">Tidak ada pesanan yang menunggu.</p>}
        </div>
      )}
    </div>
  );
}
